import axiosInstance from "api/axiosInstance";
import API from "api/endpoints";
import { ReportPeriodTypeEnum } from "Constants/Enums/ReportPeriodTypeEnum";

const DepartmentAttendanceService = () => {
  const getDepartments = async () => {
    try {
      const response = await axiosInstance.get(API.department.getList);
      let departments = response.data;

      // Handle different response formats
      if (departments && !Array.isArray(departments)) {
        departments = departments.data || departments.items || departments.departments || [];
      }

      return Array.isArray(departments)
        ? departments.map((dept) => ({
            id: dept.id || dept.departmentId,
            name: dept.name || dept.departmentName,
          }))
        : [];
    } catch (error) {
      console.error("Error fetching departments:", error);
      return [];
    }
  };

  const getDepartmentAttendance = async (departmentId, viewType = 0) => {
    try {
      if (!departmentId) {
        console.error("Department ID is required");
        return { records: [] };
      }

      const endpoint = API.attendance.getDepartmentAttendance(departmentId, viewType);

      const response = await axiosInstance.get(endpoint);
      return response.data || { records: [] };
    } catch (error) {
      console.error("Error fetching department attendance:", error);
      if (error.response) {
        console.error("Error response data:", error.response.data);
        console.error("Error response status:", error.response.status);
      }
      return { records: [] };
    }
  };

  const getEmployees = async (departmentId = null) => {
    try {
      const response = await axiosInstance.get(API.user.getList(departmentId));
      let employees = response.data;

      // Handle different response formats
      if (employees && !Array.isArray(employees)) {
        employees =
          employees.data || employees.items || employees.users || employees.employees || [];
      }

      return Array.isArray(employees)
        ? employees.map((emp) => ({
            id: emp.id || emp.userId,
            name: emp.name || emp.fullName,
            department: emp.department,
          }))
        : [];
    } catch (error) {
      console.error("Error fetching employees:", error);
      return [];
    }
  };

  // Additional method for individual employee attendance
  const getEmployeeAttendance = async (employeeId, viewType, startDate, endDate) => {
    try {
      const url = API.attendance.userAttendance(employeeId, viewType, startDate, endDate);

      const response = await axiosInstance.get(url);

      return response.data || { records: [] };
    } catch (error) {
      console.error("Error fetching employee attendance:", error);
      return { records: [] };
    }
  };

  const getWorkLifeBalanceReport = async (departmentId, month, year) => {
    try {
      // Point 13: WLB uses the day/week/month contract. Keep the page's
      // month/year selector but translate to periodType=Month + referenceDate
      // (first of that month). Response is { period, data }; return the payload
      // (the page extracts employees from it).
      const referenceDate =
        month && year ? `${year}-${String(month).padStart(2, "0")}-01` : null;
      const endpoint = API.workLifeBalance.getDepartmentMonthlyReport(
        departmentId,
        ReportPeriodTypeEnum.Month,
        referenceDate
      );
      const response = await axiosInstance.get(endpoint);
      const body = response.data;
      return body && body.data !== undefined ? body.data : body || { employees: [] };
    } catch (error) {
      console.error(" Error fetching work-life balance data:", error);
      console.error("Error details:", error.response?.data);
      return { employees: [] };
    }
  };

  // Get active working hours
  const getActiveWorkingHours = async () => {
    try {
      const response = await axiosInstance.get(API.attendance.getActiveWorkingHours);
      return response.data;
    } catch (error) {
      console.error("Error fetching active working hours:", error);
      return null;
    }
  };

  // Add/update working hours
  const addWorkingHours = async (settings) => {
    try {
      const response = await axiosInstance.post(API.attendance.addWorkingHrs, settings);
      return response.data;
    } catch (error) {
      console.error("Error updating working hours:", error);
      throw error;
    }
  };

  return {
    getDepartments,
    getDepartmentAttendance,
    getEmployees,
    getEmployeeAttendance,
    getWorkLifeBalanceReport,
    getActiveWorkingHours,
    addWorkingHours,
  };
};

export default DepartmentAttendanceService;
